import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useBlocker } from "react-router";

interface GuardContextValue {
  blocker: ReturnType<typeof useBlocker>;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  setGuardOwnerMounted: (value: boolean) => void;
  pendingAction: (() => void) | null;
  requestAction: (action: () => void) => void;
  clearAction: () => void;
}

interface WorkspaceNavigationGuardProviderProps {
  children: ReactNode;
}

const WorkspaceNavigationGuardContext = createContext<GuardContextValue | null>(
  null,
);

export function WorkspaceNavigationGuardProvider({
  children,
}: WorkspaceNavigationGuardProviderProps) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [guardOwnerMounted, setGuardOwnerMounted] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const blocker = useBlocker(guardOwnerMounted && hasUnsavedChanges);
  const value = useMemo(
    () => ({
      blocker,
      hasUnsavedChanges,
      setHasUnsavedChanges,
      setGuardOwnerMounted,
      pendingAction,
      requestAction: setPendingAction,
      clearAction: () => setPendingAction(null),
    }),
    [blocker, hasUnsavedChanges, pendingAction],
  );

  return (
    <WorkspaceNavigationGuardContext.Provider value={value}>
      {children}
    </WorkspaceNavigationGuardContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context hook is intentionally colocated with its provider
export function useWorkspaceNavigationGuard() {
  const context = useContext(WorkspaceNavigationGuardContext);
  if (!context) {
    throw new Error(
      "useWorkspaceNavigationGuard must be used inside WorkspaceNavigationGuardProvider",
    );
  }
  return context;
}

interface WorkspaceGuardActions {
  isModalOpen: boolean;
  isSaving: boolean;
  runGuardedAction: (action: () => void | Promise<void>) => Promise<void>;
  handleDiscard: () => void;
  handleCancel: () => void;
}

/**
 * Owns the navigation-guard glue for a workspace screen: reports the guard
 * owner as mounted, keeps the guard's unsaved-changes flag in sync, and exposes
 * the modal state plus the discard/cancel/save actions.
 */
// eslint-disable-next-line react-refresh/only-export-components -- guard hook is intentionally colocated with its provider
export function useWorkspaceGuardActions(
  hasUnsavedChanges: boolean,
): WorkspaceGuardActions {
  const {
    blocker,
    setHasUnsavedChanges,
    setGuardOwnerMounted,
    pendingAction,
    clearAction,
  } = useWorkspaceNavigationGuard();
  const [isSaving, setIsSaving] = useState(false);
  const isModalOpen = blocker.state === "blocked" || pendingAction !== null;

  const runGuardedAction = async (action: () => void | Promise<void>) => {
    setIsSaving(true);
    try {
      await action();
      blocker.proceed?.();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    pendingAction?.();
    clearAction();
    blocker.proceed?.();
  };

  const handleCancel = () => {
    clearAction();
    blocker.reset?.();
  };

  useEffect(() => {
    setGuardOwnerMounted(true);
    return () => {
      setGuardOwnerMounted(false);
      setHasUnsavedChanges(false);
    };
  }, [setGuardOwnerMounted, setHasUnsavedChanges]);

  useEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  return {
    isModalOpen,
    isSaving,
    runGuardedAction,
    handleDiscard,
    handleCancel,
  };
}
